import AddIcon from '@mui/icons-material/Add'
import {
  Button,
  Card,
  CardContent,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useQuotations } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatMoney,
  formatNumber,
  humanise,
  PageHeader,
  StatusChip,
} from '@/components/common'

const STATUSES = [
  'DRAFT',
  'INTERNAL_APPROVAL',
  'APPROVED',
  'SENT',
  'ACCEPTED_AWAITING_CONFIRMATION',
  'CUSTOMER_ACCEPTED',
  'CUSTOMER_REJECTED',
  'EXPIRED',
  'REVISED',
]

export function QuotationsPage() {
  const navigate = useNavigate()
  const canWrite = useAuthStore((state) => state.canWrite)
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)

  const { data, isLoading, error } = useQuotations({
    status: status || undefined,
    q: q || undefined,
    page,
    size,
  })

  return (
    <>
      <PageHeader
        title="Quotations"
        subtitle="Versions, approvals, customer decisions and conversion to orders"
        actions={
          canWrite('quotation') ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/quotations/new')}>
              New quotation
            </Button>
          ) : null
        }
      />
      <ErrorBanner error={error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search quotation number"
              value={q}
              onChange={(event) => {
                setQ(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 260 }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.content.length === 0 ? (
          <EmptyState
            message="No quotations here yet."
            hint="Raise one from an enquiry once the requirement is collected."
          />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quotation</TableCell>
                  <TableCell align="right">Version</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Event date</TableCell>
                  <TableCell align="right">Guests</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Valid until</TableCell>
                  <TableCell>Order</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.content.map((quotation) => (
                  <TableRow key={quotation.id} hover>
                    <TableCell>
                      <Link component={RouterLink} to={`/quotations/${quotation.id}`} underline="hover">
                        {quotation.quotationNumber}
                      </Link>
                    </TableCell>
                    <TableCell align="right">v{quotation.versionNumber}</TableCell>
                    <TableCell>{quotation.customerName}</TableCell>
                    <TableCell>{formatDate(quotation.eventDate)}</TableCell>
                    <TableCell align="right">{formatNumber(quotation.guestCount)}</TableCell>
                    <TableCell>
                      <StatusChip status={quotation.status} />
                    </TableCell>
                    <TableCell align="right">{formatMoney(quotation.totalAmount)}</TableCell>
                    <TableCell>{formatDate(quotation.validUntil)}</TableCell>
                    <TableCell>
                      {quotation.convertedOrderId ? (
                        <Link
                          component={RouterLink}
                          to={`/orders/${quotation.convertedOrderId}`}
                          underline="hover"
                        >
                          Open order
                        </Link>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not converted
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && (
              <TablePagination
                component="div"
                count={data.totalElements}
                page={data.page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) => {
                  setSize(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </>
        )}
      </Card>
    </>
  )
}
